import { EditorialSpreadPublicationControlsV1 } from "./EditorialSpreadPublicationControlsV1";
/**
 * Editorial Authoring V2 — VISUAL_STORY authoring (BUILD 4, corrected
 * against the real VisualStoryMaster.jsx Reader contract).
 *
 * Rendered by EditorialSpreadWorkspaceV2.jsx in place of the generic
 * workspace body when block.master_type === "VISUAL_STORY".
 * Self-contained: owns its own modal chrome, save flow and save-state
 * display. No recognition/parser step — this is direct manual
 * authoring of photos + text.
 *
 * No import from @/components/editorial/ (the legacy authoring UI, or
 * VisualStoryMaster/HeroMaster specifically). Allowed imports:
 * @/lib/* (including editorialReaderLayout.mjs's editorialImageStyle,
 * explicitly permitted for this exact purpose), and this app's own
 * @/components/editorial-v2/* siblings (EditorialMediaPickerV2,
 * EditorialCropV2).
 *
 * ── text_blocks DATA SHAPE — CRITICAL ────────────────────────────────
 * The real Reader contract is content.text_blocks: { id, body }[],
 * rendered via tb.body — NOT a plain string[]. This file preserves
 * that object shape end to end: local state, add/edit/reorder/remove,
 * and Save all operate on { id, body } objects. Only `body` is ever
 * edited on an existing entry; `id` is created once (newBlockId()) and
 * carried through untouched. The legacy content.body fallback seeds
 * exactly one local object, id "b1", matching the confirmed legacy
 * normalisation — and only locally, never written back until Save.
 *
 * ── images DATA SHAPE (unchanged from the original confirmed
 *    contract) ───────────────────────────────────────────────────────
 *   content.images[] — 1–5 entries: { media_id, url, focal_x, focal_y,
 *   zoom, alt }. Order is preserved through reorder/add/remove.
 *
 * ── DESKTOP / MOBILE COLLAGE — mirrors the real VisualStoryMaster
 *    layout exactly (12-column grid, auto-placement) ─────────────────
 * Desktop: 1 image full-bleed (aspect 4:5, maxHeight 680, no forced
 * crop) · 2 images (col-span-7/row-span-2 aspect 3:4 + col-span-5
 * pt-10 aspect 4:5, maxHeight 60vh) · 3–4 images: a packed
 * col-span-12 grid (aspect 5:6, maxHeight 60vh) with img1 at
 * col-span-7/row-span-2 and the rest auto-placed into the remaining
 * cells by the browser's normal CSS Grid auto-flow (col-span-5+5 for
 * 3 images; col-span-5+3+2 for 4) · 5 images: a 3-row mosaic
 * (aspect 3:4, maxHeight 60vh), row1 8+4, row2 5+7, row3 12 — again
 * relying on plain grid auto-placement in DOM order, no manual
 * grid-area assignment needed since each row's spans sum to exactly
 * 12. Desktop page composition: collage column / text column as
 * minmax(0,3fr) minmax(0,2fr), gap-10 (lg: gap-14) — scaled to fit the
 * compact preview modal rather than the real 1200px page width, same
 * ratio.
 * Mobile: 1 image full-bleed (aspect 4:5, maxHeight 420, no forced
 * crop) · 2–5 images: two-column grid (gap-2), lead image spans both
 * columns at its own intrinsic ratio (no forced aspect, no forced
 * crop), remaining images normally aspect 5:4 with
 * maxHeight: min(38vh, 260px) — except when there are exactly 4 images
 * total (3 "remaining" tiles), the first remaining tile instead spans
 * 2 rows at aspect 4:6. Mobile content order: eyebrow → headline →
 * subtitle → collage → text_blocks → quote → signature → caption.
 *
 * ── IMAGE RENDERING ───────────────────────────────────────────────────
 * Every collage/single-image <img> gets its style from the real
 * editorialImageStyle(media, { intentionalCrop }) helper — imported
 * directly, not reimplemented — so contain-vs-cover / focal position /
 * zoom transform exactly match Reader semantics. intentionalCrop is
 * passed false for the single-image slot (matching the confirmed
 * contract precisely); true for ordinary collage tiles, where a tight
 * crop is the intended aesthetic. The helper's own "explicit crop"
 * detection (reading the image's own focal_x/focal_y/zoom) still
 * applies underneath either way. The MOBILE LEAD image (image count
 * 2–5) is the one deliberate exception: it does NOT call
 * editorialImageStyle at all — no focal/zoom transform, plain
 * object-contain at its own intrinsic ratio — because Reader treats it
 * differently from every other slot (see ImgBox's
 * preserveIntrinsicRatio path).
 *
 * ── CROP AUTHORING ───────────────────────────────────────────────────
 * EditorialCropV2 is reused, but NOT with a claimed universal 4:5
 * VISUAL_STORY frame — a single image can appear in several different
 * Reader geometries depending on image count/position/device. The
 * crop modal here is a neutral focal/zoom editing surface only
 * (aspectRatio 1, frameLabel "Фокус изображения"); the Desktop/Mobile
 * local preview below is the actual source of truth for how that crop
 * behaves in each collage position. No separate mobile crop field is
 * introduced.
 *
 * ── COLOR / BACKGROUND ────────────────────────────────────────────────
 * Palette: real HERO_PALETTES swatch+accent values (PALETTES below).
 * Preview eyebrow, quote border and signature use palette.accent —
 * never the swatch. Background: real HERO_BACKGROUNDS values via
 * settings.background, including "full-image" → transparent, which is
 * NOT converted back to a solid color (only an unrecognized key falls
 * back to warm-ivory). Headline/subtitle/body/caption use their own
 * fixed Reader colors (HEADLINE_COLOR/SUBTITLE_COLOR/BODY_COLOR/
 * CAPTION_COLOR below) — not background-conditional. settings.palette
 * / settings.background default to index 0 of each (ivory /
 * warm-ivory — different from EDITORIAL_STORY's HERO_PALETTES[2]
 * olive default). Tokens are duplicated locally as a small V2-only
 * mirror (same pattern as HERO/EDITORIAL_STORY V2), not imported from
 * HeroMaster. No new settings are written.
 *
 * ── SAVE CONTRACT ─────────────────────────────────────────────────────
 * Persists content.images, content.eyebrow, content.headline,
 * content.subtitle, content.text_blocks ({id,body}[]), content.quote,
 * content.signature, content.caption, merged into the block's existing
 * content. content.body is deliberately NOT overwritten — the
 * canonical V2 text is content.text_blocks. content.media_ids is
 * mirrored from ALL current images' media_ids, in order (not just the
 * first) — not a new field, purely so the existing, unmodified gallery
 * thumbnail-resolution fallback keeps working; no backend field is
 * introduced.
 *
 * ── COMPATIBILITY BRIDGE (no destructive rewrite before Save) ─────────
 *   content.images present            → used as-is.
 *   only legacy content.media_ids     → local image list seeded from
 *                                        the ids, URLs resolved via the
 *                                        existing GET /admin/media +
 *                                        mediaRecordUrl.
 *   only content.body, no text_blocks → one local { id:"b1", body }
 *                                        text block seeded from it.
 *
 * Provider calls = 0. AI cost = $0.
 */
import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { mediaRecordUrl } from "@/lib/editorialMedia.mjs";
import { editorialImageStyle } from "@/lib/editorialReaderLayout.mjs";
import { MediaLibraryPickerV2, MEDIA_LIST, MEDIA_UPLOAD } from "./EditorialMediaPickerV2";
import { EditorialCropV2 } from "./EditorialCropV2";
import { PublicationDestinationV2 } from "./PublicationDestinationV2";

const MAX_IMAGES = 5;

// Crop authoring is a neutral focal/zoom surface — see file header.
const CROP_ASPECT = 1;
const CROP_FRAME_LABEL = "Фокус изображения";

// ── Reader design tokens (V2-only mirror, not an import) ─────────────
// Real HERO_PALETTES swatch + accent values. Preview eyebrow / quote
// border / signature use palette.accent — never the swatch — per the
// confirmed Reader contract. VISUAL_STORY's own confirmed fallback is
// index 0 (ivory) — different from EDITORIAL_STORY's HERO_PALETTES[2]
// (olive) default.
const PALETTES = {
  ivory: { swatch: "#EFE6D5", accent: "#8A6A1E" },
  almond: { swatch: "#D6B98A", accent: "#8A5A2E" },
  olive: { swatch: "#7E8A3E", accent: "#4B5522" },
  cocoa: { swatch: "#5B3B25", accent: "#F1E4CB" },
  burgundy: { swatch: "#7A2A2A", accent: "#F1E4CB" },
  gold: { swatch: "#C9A84C", accent: "#3B241A" },
  pistachio: { swatch: "#A6C088", accent: "#3B241A" },
  apricot: { swatch: "#E8B48A", accent: "#5B3B25" },
};
const BACKGROUNDS = {
  "warm-ivory": "#EFE6D5",
  "mineral-plaster": "#E6DFCF",
  "almond-paper": "#F1E7D2",
  "deep-cocoa": "#2A1810",
  "full-image": "transparent",
};

// Real Reader text-color contract (fixed, not background-conditional).
const HEADLINE_COLOR = "#2A1D14";
const SUBTITLE_COLOR = "#5B4326";
const BODY_COLOR = "#3E2E20";
const CAPTION_COLOR = "#5B4326";

function paletteAccent(key) { return (PALETTES[key] || PALETTES.ivory).accent; }
function backgroundColor(key) {
  // Real Reader: full-image → transparent. Only fall back to
  // warm-ivory when the key itself is unknown — never convert a
  // legitimate "transparent" value back to a solid color.
  return BACKGROUNDS[key] || BACKGROUNDS["warm-ivory"];
}

function normalizeImage(img) {
  return {
    media_id: img?.media_id || null,
    url: img?.url || null,
    focal_x: img?.focal_x ?? 0.5,
    focal_y: img?.focal_y ?? 0.5,
    zoom: img?.zoom ?? 1,
    alt: img?.alt || "",
  };
}

// Compatibility bridge for images — see file header.
function deriveInitialImages(content) {
  const c = content || {};
  if (Array.isArray(c.images) && c.images.length) {
    return c.images.slice(0, MAX_IMAGES).map(normalizeImage);
  }
  if (Array.isArray(c.media_ids) && c.media_ids.length) {
    return c.media_ids.slice(0, MAX_IMAGES).map((id) => normalizeImage({ media_id: id }));
  }
  return [];
}

let blockIdSeq = 0;
function newBlockId() {
  blockIdSeq += 1;
  return `tb-${Date.now().toString(36)}-${blockIdSeq}`;
}

// Compatibility bridge for text — see file header. Real Reader shape:
// { id, body }[]. Existing object entries are preserved as-is; the
// legacy content.body fallback seeds exactly [{ id: "b1", body }].
function deriveInitialTextBlocks(content) {
  const c = content || {};
  if (Array.isArray(c.text_blocks) && c.text_blocks.length) {
    return c.text_blocks.map((b, i) => ({
      id: b?.id || `tb-legacy-${i}`,
      body: typeof b === "string" ? b : String(b?.body || ""),
    }));
  }
  if (String(c.body || "").trim()) {
    return [{ id: "b1", body: String(c.body) }];
  }
  return [];
}

// ── Image rendering — mirrors Reader's editorialImageStyle semantics.
// aspect: CSS aspect-ratio string, e.g. "4/5". Omit together with
// `fill` for intrinsic-ratio display (the mobile lead image).
// fill: stretch to the parent grid cell's own shape instead of a
// fixed aspect-ratio (used by the packed/mosaic desktop tiles).
function ImgBox({ img, aspect, maxHeight, intentionalCrop, fill, preserveIntrinsicRatio }) {
  // Mobile lead image (image count 2–5): mirrors Reader exactly —
  // no editorialImageStyle call at all, no focal/zoom transform, just
  // the image at its own intrinsic ratio with object-contain. This is
  // deliberately distinct from single-image mode, which keeps the
  // existing Reader behavior (aspect 4:5 + editorialImageStyle with
  // intentionalCrop=false, further down).
  if (preserveIntrinsicRatio) {
    return (
      <div className="overflow-hidden bg-espresso/10">
        {img?.url ? (
          <img src={img.url} alt={img.alt || ""} className="block h-auto w-full object-contain" />
        ) : (
          <div className="aspect-[4/5] w-full" />
        )}
      </div>
    );
  }

  const style = img?.url ? editorialImageStyle(img, { intentionalCrop: Boolean(intentionalCrop) }) : null;

  if (!aspect && !fill) {
    return (
      <div className="overflow-hidden bg-espresso/10">
        {img?.url ? (
          <img src={img.url} alt={img.alt || ""} className="block h-auto w-full" style={style} />
        ) : (
          <div className="aspect-[4/5] w-full" />
        )}
      </div>
    );
  }

  const boxStyle = {};
  if (aspect) boxStyle.aspectRatio = aspect;
  if (maxHeight != null) boxStyle.maxHeight = typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight;

  return (
    <div className={`relative overflow-hidden bg-espresso/10 ${fill ? "h-full w-full" : "w-full"}`}
      style={boxStyle}>
      {img?.url && (
        <img src={img.url} alt={img.alt || ""} className="absolute inset-0 h-full w-full" style={style} />
      )}
    </div>
  );
}

// Desktop collage — see file header for the exact per-count contract.
function DesktopCollage({ images }) {
  const n = images.length;

  if (n === 0) {
    return <ImgBox img={null} aspect="4/5" maxHeight={680} intentionalCrop={false} />;
  }
  if (n === 1) {
    return <ImgBox img={images[0]} aspect="4/5" maxHeight={680} intentionalCrop={false} />;
  }
  if (n === 2) {
    return (
      <div className="grid grid-cols-12 gap-2" style={{ maxHeight: "60vh" }}>
        <div className="col-span-7 row-span-2">
          <ImgBox img={images[0]} aspect="3/4" intentionalCrop />
        </div>
        <div className="col-span-5 pt-10">
          <ImgBox img={images[1]} aspect="4/5" intentionalCrop />
        </div>
      </div>
    );
  }
  if (n === 3) {
    return (
      <div className="grid grid-cols-12 grid-rows-2 gap-2" style={{ aspectRatio: "5/6", maxHeight: "60vh" }}>
        <div className="col-span-7 row-span-2"><ImgBox img={images[0]} intentionalCrop fill /></div>
        <div className="col-span-5"><ImgBox img={images[1]} intentionalCrop fill /></div>
        <div className="col-span-5"><ImgBox img={images[2]} intentionalCrop fill /></div>
      </div>
    );
  }
  if (n === 4) {
    return (
      <div className="grid grid-cols-12 grid-rows-2 gap-2" style={{ aspectRatio: "5/6", maxHeight: "60vh" }}>
        <div className="col-span-7 row-span-2"><ImgBox img={images[0]} intentionalCrop fill /></div>
        <div className="col-span-5"><ImgBox img={images[1]} intentionalCrop fill /></div>
        <div className="col-span-3"><ImgBox img={images[2]} intentionalCrop fill /></div>
        <div className="col-span-2"><ImgBox img={images[3]} intentionalCrop fill /></div>
      </div>
    );
  }
  // n === 5
  return (
    <div className="grid grid-cols-12 grid-rows-3 gap-2" style={{ aspectRatio: "3/4", maxHeight: "60vh" }}>
      <div className="col-span-8"><ImgBox img={images[0]} intentionalCrop fill /></div>
      <div className="col-span-4"><ImgBox img={images[1]} intentionalCrop fill /></div>
      <div className="col-span-5"><ImgBox img={images[2]} intentionalCrop fill /></div>
      <div className="col-span-7"><ImgBox img={images[3]} intentionalCrop fill /></div>
      <div className="col-span-12"><ImgBox img={images[4]} intentionalCrop fill /></div>
    </div>
  );
}

// Mobile collage — see file header for the exact contract, including
// the "4 total images" special case.
function MobileCollage({ images }) {
  const n = images.length;
  if (n === 0) return <ImgBox img={null} aspect="4/5" maxHeight={420} intentionalCrop={false} />;
  if (n === 1) return <ImgBox img={images[0]} aspect="4/5" maxHeight={420} intentionalCrop={false} />;

  const lead = images[0];
  const rest = images.slice(1);

  return (
    <div className="grid grid-cols-2 gap-2">
      <div className="col-span-2">
        <ImgBox img={lead} preserveIntrinsicRatio />
      </div>
      {rest.map((img, i) => {
        const isSpecialFirst = rest.length === 3 && i === 0;
        return (
          <div key={i} className={isSpecialFirst ? "row-span-2" : ""}>
            <ImgBox
              img={img}
              aspect={isSpecialFirst ? "4/6" : "5/4"}
              maxHeight={isSpecialFirst ? undefined : "min(38vh, 260px)"}
              intentionalCrop
              fill={isSpecialFirst}
            />
          </div>
        );
      })}
    </div>
  );
}

function TextColumn({ eyebrow, headline, subtitle, textBlocks, quote, signature, caption, compact, accentColor }) {
  const bodies = textBlocks.map((b) => b.body).filter(Boolean);
  const shown = compact ? bodies.slice(0, 3) : bodies;
  const hasMore = compact && bodies.length > 3;
  return (
    <div>
      {eyebrow && <div className="text-[10px] uppercase tracking-widest" style={{ color: accentColor }}>{eyebrow}</div>}
      {headline && <div className="mt-1 font-serif text-xl leading-snug" style={{ color: HEADLINE_COLOR }}>{headline}</div>}
      {subtitle && <div className="mt-2 font-serif text-sm" style={{ color: SUBTITLE_COLOR }}>{subtitle}</div>}
      {shown.length > 0 && (
        <div className="mt-3 space-y-2">
          {shown.map((t, i) => <p key={i} className="text-xs leading-relaxed" style={{ color: BODY_COLOR }}>{t}</p>)}
          {hasMore && <div className="text-[10px] italic" style={{ color: BODY_COLOR, opacity: 0.7 }}>Текст продолжается…</div>}
        </div>
      )}
      {quote && (
        <div className="mt-3 border-l-2 pl-3 font-serif text-sm italic" style={{ color: BODY_COLOR, borderColor: accentColor }}>
          «{quote}»
        </div>
      )}
      {signature && <div className="mt-1 text-xs" style={{ color: accentColor }}>{signature}</div>}
      {caption && <div className="mt-2 text-[11px] italic" style={{ color: CAPTION_COLOR, opacity: 0.7 }}>{caption}</div>}
    </div>
  );
}

export function EditorialVisualStoryV2({ block, volume, onClose, onSaved, publicationDirty, onPublicationInput }) {
  const c = block.content || {};
  const s = block.settings || {};

  const [images, setImages] = useState(deriveInitialImages(c));
  const [eyebrow, setEyebrow] = useState(c.eyebrow || "");
  const [headline, setHeadline] = useState(c.headline || "");
  const [subtitle, setSubtitle] = useState(c.subtitle || "");
  const [textBlocks, setTextBlocks] = useState(deriveInitialTextBlocks(c));
  const [quote, setQuote] = useState(c.quote || "");
  const [signature, setSignature] = useState(c.signature || "");
  const [caption, setCaption] = useState(c.caption || "");

  const [libraryTarget, setLibraryTarget] = useState(null); // "add" or image index
  const [uploadTarget, setUploadTarget] = useState(null);   // "add" or image index
  const [uploading, setUploading] = useState(false);
  const [cropIndex, setCropIndex] = useState(null);
  const fileInputRef = useRef(null);

  const [showPreview, setShowPreview] = useState(false);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [saveState, setSaveState] = useState("idle");

  useEffect(() => {
    setImages(deriveInitialImages(block.content || {}));
    setEyebrow(block.content?.eyebrow || "");
    setHeadline(block.content?.headline || "");
    setSubtitle(block.content?.subtitle || "");
    setTextBlocks(deriveInitialTextBlocks(block.content || {}));
    setQuote(block.content?.quote || "");
    setSignature(block.content?.signature || "");
    setCaption(block.content?.caption || "");
    setLibraryTarget(null);
    setUploadTarget(null);
    setSaveState("idle");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id]);

  // Resolve legacy media_ids-derived images that have no known URL yet.
  useEffect(() => {
    const hasUnresolved = images.some((img) => img.media_id && !img.url);
    if (!hasUnresolved) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(MEDIA_LIST);
        if (cancelled) return;
        const records = Array.isArray(data) ? data : (data?.items || data?.results || []);
        const byId = new Map(records.map((r) => [r.id, r]));
        setImages((imgs) => imgs.map((img) => {
          if (img.url || !img.media_id) return img;
          const record = byId.get(img.media_id);
          const url = record ? mediaRecordUrl(record) : null;
          return url ? { ...img, url } : img;
        }));
      } catch {
        // Non-fatal: unresolved images just show no thumbnail yet.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id]);  const atLimit = images.length >= MAX_IMAGES;
  const storyPalette = s.palette || "ivory";
  const storyBackground = s.background || "warm-ivory";

  const addImage = (image) => {
    if (images.length >= MAX_IMAGES) return;
    setImages((imgs) => [...imgs, normalizeImage(image)]);
  };

  const replaceImage = (index, image) => {
    setImages((imgs) => imgs.map((img, i) => (
      i === index
        ? normalizeImage({ ...image, alt: "", focal_x: 0.5, focal_y: 0.5, zoom: 1 })
        : img
    )));
  };

  const openUpload = (target) => {
    setUploadTarget(target);
    fileInputRef.current?.click();
  };

  const pickFromLibrary = (media) => {
    const url = mediaRecordUrl(media) || media.file_url || media.url || null;
    const nextImage = { media_id: media.id, url };
    if (typeof libraryTarget === "number") replaceImage(libraryTarget, nextImage);
    else addImage(nextImage);
    setLibraryTarget(null);
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const target = uploadTarget;
    setUploadTarget(null);
    if (!file) return;
    if (target === "add" && images.length >= MAX_IMAGES) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(MEDIA_UPLOAD, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = mediaRecordUrl(data) || data.file_url || data.url || null;
      const nextImage = { media_id: data.id, url };
      if (typeof target === "number") replaceImage(target, nextImage);
      else addImage(nextImage);
      toast.success(typeof target === "number" ? "Фото заменено" : "Фото загружено");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => setImages((imgs) => imgs.filter((_, i) => i !== index));
  const moveImage = (index, dir) => {
    setImages((imgs) => {
      const to = index + dir;
      if (to < 0 || to >= imgs.length) return imgs;
      const next = [...imgs];
      [next[index], next[to]] = [next[to], next[index]];
      return next;
    });
  };
  const updateAlt = (index, value) => {
    setImages((imgs) => imgs.map((img, i) => (i === index ? { ...img, alt: value } : img)));
  };
  const saveCrop = (values) => {
    if (cropIndex === null) return;
    setImages((imgs) => imgs.map((img, i) => (i === cropIndex ? { ...img, ...values } : img)));
    setCropIndex(null);
  };

  const addTextBlock = () => setTextBlocks((blocks) => [...blocks, { id: newBlockId(), body: "" }]);
  const updateTextBlock = (index, value) => {
    setTextBlocks((blocks) => blocks.map((b, i) => (i === index ? { ...b, body: value } : b)));
  };
  const removeTextBlock = (index) => setTextBlocks((blocks) => blocks.filter((_, i) => i !== index));
  const moveTextBlock = (index, dir) => {
    setTextBlocks((blocks) => {
      const to = index + dir;
      if (to < 0 || to >= blocks.length) return blocks;
      const next = [...blocks];
      [next[index], next[to]] = [next[to], next[index]];
      return next;
    });
  };

  const save = async () => {
    setSaveState("saving");
    try {
      const nextContent = {
        ...c,
        images: images.map((img) => ({
          media_id: img.media_id,
          url: img.url,
          focal_x: img.focal_x,
          focal_y: img.focal_y,
          zoom: img.zoom,
          alt: img.alt,
        })),
        eyebrow,
        headline,
        subtitle,
        text_blocks: textBlocks.map((b) => ({ id: b.id, body: b.body })),
        quote,
        signature,
        caption,
        // Mirrors ALL current images' media_ids, in order — reuses the
        // existing generic field so the gallery's existing fallback
        // thumbnail resolution keeps working; see file header.
        media_ids: images.map((img) => img.media_id).filter(Boolean),
      };
      await api.patch(`/admin/editorial/blocks/${block.id}`, { content: nextContent });
      setSaveState("saved");
      const savedMediaItems = images
        .filter((img) => img.media_id && img.url)
        .map((img) => ({ id: img.media_id, url: img.url }));
      onSaved?.({ ...block, content: nextContent }, savedMediaItems);
      toast.success("Изменения сохранены");
      setTimeout(() => setSaveState("idle"), 1500);
    } catch (e) {
      setSaveState("error");
      toast.error(formatApiError(e));
    }
  };

  const saveLabel = {
    idle: "", saving: "Сохранение…", saved: "Сохранено", error: "Ошибка сохранения",
  }[saveState];

  const title = headline || "Визуальная история";
  const cropImage = cropIndex !== null ? images[cropIndex] : null;

  return (
    <div
      onInputCapture={onPublicationInput} onChangeCapture={onPublicationInput}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="editorial-v2-visualstory-workspace"
    >
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl bg-cream">
        <div className="flex items-start justify-between gap-3 border-b border-espresso/10 px-5 py-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-espresso/55">Визуальная история</div>
            <div className="mt-0.5 truncate font-serif text-xl text-espresso">{title}</div>
          </div>
          {saveLabel && (
            <span className="shrink-0 pt-1 text-[11px] uppercase tracking-widest text-espresso/50"
              data-testid="editorial-v2-visualstory-save-state">
              {saveLabel}
            </span>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          <PublicationDestinationV2 materialId={block.material_id} volume={volume} />

          {/* Photos */}
          <div>
            <label className="mb-2 block text-[11px] text-espresso/65">
              Фотографии {images.length > 0 && `(${images.length} из ${MAX_IMAGES})`}
            </label>

            {images.length > 0 && (
              <div className="mb-3 space-y-2">
                {images.map((img, i) => (
                  <div key={i} className="flex items-start gap-3 rounded-xl border border-espresso/10 bg-white/60 p-2"
                    data-testid={`editorial-v2-visualstory-image-${i}`}>
                    <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-espresso/10">
                      {img.url && (
                        <img src={img.url} alt={img.alt || ""} className="h-full w-full object-cover"
                          style={{ objectPosition: `${img.focal_x * 100}% ${img.focal_y * 100}%` }} />
                      )}
                      {i === 0 && (
                        <span className="absolute left-0.5 top-0.5 rounded bg-espresso/80 px-1 text-[8px] uppercase tracking-wide text-cream">
                          Главное
                        </span>
                      )}
                    </div>
                    <div className="flex-1 space-y-1.5">
                      <input value={img.alt} onChange={(e) => updateAlt(i, e.target.value)}
                        placeholder="Описание изображения (alt)"
                        className="w-full rounded-lg border border-espresso/20 px-2 py-1 text-xs"
                        data-testid={`editorial-v2-visualstory-alt-${i}`} />
                      <div className="flex flex-wrap gap-1.5">
                        <button type="button" onClick={() => openUpload(i)} disabled={uploading}
                          className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso disabled:opacity-50"
                          data-testid={`editorial-v2-visualstory-replace-${i}`}>
                          {img.url ? "Заменить фото" : "Загрузить фото"}
                        </button>
                        <button type="button" onClick={() => setLibraryTarget(i)}
                          className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso"
                          data-testid={`editorial-v2-visualstory-library-${i}`}>
                          Из Медиатеки
                        </button>
                        <button type="button" onClick={() => setCropIndex(i)} disabled={!img.url}
                          className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso disabled:opacity-30"
                          data-testid={`editorial-v2-visualstory-crop-${i}`}>
                          Настроить кадр
                        </button>
                        <button type="button" onClick={() => moveImage(i, -1)} disabled={i === 0}
                          className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso disabled:opacity-30"
                          data-testid={`editorial-v2-visualstory-up-${i}`}>
                          ▲
                        </button>
                        <button type="button" onClick={() => moveImage(i, 1)} disabled={i === images.length - 1}
                          className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso disabled:opacity-30"
                          data-testid={`editorial-v2-visualstory-down-${i}`}>
                          ▼
                        </button>
                        <button type="button" onClick={() => removeImage(i)}
                          className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso/70"
                          data-testid={`editorial-v2-visualstory-remove-${i}`}>
                          Удалить
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!atLimit ? (
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => openUpload("add")} disabled={uploading}
                  className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso disabled:opacity-50"
                  data-testid="editorial-v2-visualstory-add-photo">
                  {uploading ? "Загрузка…" : "Добавить фотографию"}
                </button>
                <button type="button" onClick={() => setLibraryTarget("add")}
                  className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso"
                  data-testid="editorial-v2-visualstory-open-library">
                  Из Медиатеки
                </button>
              </div>
            ) : (
              <p className="text-xs text-espresso/50">Можно добавить до {MAX_IMAGES} фото.</p>
            )}
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
              onChange={handleFileSelected} data-testid="editorial-v2-visualstory-file-input" />
          </div>

          {/* Text */}
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-[11px] text-espresso/60">Надзаголовок</label>
              <input value={eyebrow} onChange={(e) => setEyebrow(e.target.value)}
                className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                data-testid="editorial-v2-visualstory-eyebrow" />
            </div>
            <div>
              <label className="mb-1 block text-[11px] text-espresso/60">Заголовок</label>
              <input value={headline} onChange={(e) => setHeadline(e.target.value)}
                className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                data-testid="editorial-v2-visualstory-headline" />
            </div>
            <div>
              <label className="mb-1 block text-[11px] text-espresso/60">Подзаголовок</label>
              <input value={subtitle} onChange={(e) => setSubtitle(e.target.value)}
                className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                data-testid="editorial-v2-visualstory-subtitle" />
            </div>

            <div>
              <label className="mb-1 block text-[11px] text-espresso/60">Основной текст</label>
              <div className="space-y-2">
                {textBlocks.map((b, i) => (
                  <div key={b.id} className="flex gap-2">
                    <textarea value={b.body} onChange={(e) => updateTextBlock(i, e.target.value)} rows={3}
                      className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                      data-testid={`editorial-v2-visualstory-block-${i}`} />
                    <div className="flex shrink-0 flex-col gap-1">
                      <button type="button" onClick={() => moveTextBlock(i, -1)} disabled={i === 0}
                        className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso disabled:opacity-30"
                        data-testid={`editorial-v2-visualstory-block-up-${i}`}>
                        ▲
                      </button>
                      <button type="button" onClick={() => moveTextBlock(i, 1)} disabled={i === textBlocks.length - 1}
                        className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso disabled:opacity-30"
                        data-testid={`editorial-v2-visualstory-block-down-${i}`}>
                        ▼
                      </button>
                      <button type="button" onClick={() => removeTextBlock(i)}
                        className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso/70"
                        data-testid={`editorial-v2-visualstory-block-remove-${i}`}>
                        Удалить
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <button type="button" onClick={addTextBlock}
                className="mt-2 text-xs text-espresso/60 underline underline-offset-2"
                data-testid="editorial-v2-visualstory-block-add">
                + Добавить блок текста
              </button>
            </div>

            <div>
              <label className="mb-1 block text-[11px] text-espresso/60">Цитата</label>
              <textarea value={quote} onChange={(e) => setQuote(e.target.value)} rows={2}
                className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                data-testid="editorial-v2-visualstory-quote" />
            </div>
            <div>
              <label className="mb-1 block text-[11px] text-espresso/60">Подпись к цитате</label>
              <input value={signature} onChange={(e) => setSignature(e.target.value)}
                className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                data-testid="editorial-v2-visualstory-signature" />
            </div>
            <div>
              <label className="mb-1 block text-[11px] text-espresso/60">Подпись к фотографиям</label>
              <input value={caption} onChange={(e) => setCaption(e.target.value)}
                className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                data-testid="editorial-v2-visualstory-caption" />
            </div>
          </div>

          {/* Local preview */}
          {showPreview && (
            <div className="rounded-2xl border border-espresso/15 bg-white p-4" data-testid="editorial-v2-visualstory-preview">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-espresso/50">Предпросмотр разворота</div>
                <div className="flex gap-1 rounded-full border border-espresso/15 p-0.5 text-[11px]">
                  <button type="button" onClick={() => setPreviewDevice("desktop")}
                    className={`rounded-full px-2.5 py-1 ${previewDevice === "desktop" ? "bg-espresso text-cream" : "text-espresso/60"}`}
                    data-testid="editorial-v2-visualstory-preview-desktop">
                    Desktop
                  </button>
                  <button type="button" onClick={() => setPreviewDevice("mobile")}
                    className={`rounded-full px-2.5 py-1 ${previewDevice === "mobile" ? "bg-espresso text-cream" : "text-espresso/60"}`}
                    data-testid="editorial-v2-visualstory-preview-mobile">
                    Mobile
                  </button>
                </div>
              </div>

              {previewDevice === "desktop" ? (
                <div className="mx-auto max-w-lg rounded-2xl p-4" style={{ backgroundColor: backgroundColor(storyBackground) }}>
                  <div className="grid gap-6" style={{ gridTemplateColumns: "minmax(0,3fr) minmax(0,2fr)" }}>
                    <DesktopCollage images={images} />
                    <div className="flex items-center">
                      <TextColumn eyebrow={eyebrow} headline={headline} subtitle={subtitle}
                        textBlocks={textBlocks} quote={quote} signature={signature} caption={caption}
                        compact accentColor={paletteAccent(storyPalette)} />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mx-auto max-w-[240px] space-y-3 rounded-2xl p-3" style={{ backgroundColor: backgroundColor(storyBackground) }}>
                  {(eyebrow || headline || subtitle) && (
                    <div>
                      {eyebrow && <div className="text-[10px] uppercase tracking-widest" style={{ color: paletteAccent(storyPalette) }}>{eyebrow}</div>}
                      {headline && <div className="mt-1 font-serif text-lg leading-snug" style={{ color: HEADLINE_COLOR }}>{headline}</div>}
                      {subtitle && <div className="mt-1 font-serif text-sm" style={{ color: SUBTITLE_COLOR }}>{subtitle}</div>}
                    </div>
                  )}
                  <MobileCollage images={images} />
                  {textBlocks.slice(0, 3).map((b, i) => (
                    b.body && <p key={i} className="text-xs leading-relaxed" style={{ color: BODY_COLOR }}>{b.body}</p>
                  ))}
                  {textBlocks.length > 3 && (
                    <div className="text-[10px] italic" style={{ color: BODY_COLOR, opacity: 0.7 }}>Текст продолжается…</div>
                  )}
                  {quote && (
                    <div className="border-l-2 pl-3 font-serif text-sm italic"
                      style={{ color: BODY_COLOR, borderColor: paletteAccent(storyPalette) }}>
                      «{quote}»
                    </div>
                  )}
                  {signature && <div className="text-xs" style={{ color: paletteAccent(storyPalette) }}>{signature}</div>}
                  {caption && <div className="text-[11px] italic" style={{ color: CAPTION_COLOR, opacity: 0.7 }}>{caption}</div>}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-espresso/10 px-5 py-4">
          <EditorialSpreadPublicationControlsV1 block={block} dirty={publicationDirty} />
          <button type="button" onClick={onClose}
            className="rounded-xl border border-espresso/20 bg-white px-4 py-2 text-sm text-espresso"
            data-testid="editorial-v2-visualstory-close">
            Закрыть
          </button>
          <button type="button" onClick={() => setShowPreview((v) => !v)}
            className="rounded-xl border border-espresso/20 bg-white px-4 py-2 text-sm text-espresso"
            data-testid="editorial-v2-visualstory-preview-toggle">
            {showPreview ? "Скрыть предпросмотр" : "Предпросмотр разворота"}
          </button>
          <button type="button" onClick={save} disabled={saveState === "saving"}
            className="rounded-xl bg-espresso px-4 py-2 text-sm text-cream disabled:opacity-50"
            data-testid="editorial-v2-visualstory-save">
            Сохранить изменения
          </button>
        </div>
      </div>

      <MediaLibraryPickerV2 open={libraryTarget !== null} onClose={() => setLibraryTarget(null)} onPick={pickFromLibrary} />

      <EditorialCropV2
        open={cropIndex !== null}
        url={cropImage?.url || null}
        aspectRatio={CROP_ASPECT}
        frameLabel={CROP_FRAME_LABEL}
        focalX={cropImage?.focal_x ?? 0.5}
        focalY={cropImage?.focal_y ?? 0.5}
        zoom={cropImage?.zoom ?? 1}
        onSave={saveCrop}
        onClose={() => setCropIndex(null)}
      />
    </div>
  );
}